import { ref, watch } from 'vue';

import { useRecipeStore } from '@apps/main/new-desktop/core/stores/recipe/recipeStore';
import { useTripStore } from '@apps/main/new-desktop/core/stores/trip/tripStore';
import { useCriteriaStore } from '@apps/main/new-desktop/core/stores/criteria/criteriaStore';

import type { DestinationAvailabilitySearchUserCriteria } from '@libs/trip-planner-interfaces';
import { performanceMark } from '@libs/utils';

import type TripPlannerModel from '../tripPlannerModel/TripPlannerModel';
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/explicit-module-boundary-types
export function useDestinationAvailabilityViewModel(model: TripPlannerModel) {
  const shouldSkipNextDestinationAvailabilitySearch = ref<boolean>(false);
  const recipeStore = useRecipeStore();
  const tripStore = useTripStore();
  const criteriaStore = useCriteriaStore();

  watch(
    () => criteriaStore.destinationAvailabilitySearchUserCriteria,
    (newCriteria, oldCriteria) => {
      if (!newCriteria) {
        tripStore.clearDestinationStaticInfo();
        tripStore.clearDestinationAvailability();
        return;
      }

      if (recipeStore.shouldSkipItinerarySearch) {
        return;
      }

      if (shouldSkipNextDestinationAvailabilitySearch.value) {
        shouldSkipNextDestinationAvailabilitySearch.value = false;
        return;
      }

      shouldSkipNextDestinationAvailabilitySearch.value =
        shouldSkipNextIteration(newCriteria);

      const isDraggingTheMap =
        newCriteria.constraint.polygon !== oldCriteria?.constraint.polygon;

      if (!isDraggingTheMap) {
        tripStore.clearDestinationStaticInfo();
        tripStore.clearDestinationAvailability();
      }

      model.searchDestinationAvailability(
        newCriteria,
        criteriaStore.businessCriteria
      );
    },
    { deep: true }
  );

  model.destinationAvailabilitySearches.subscribe(() => {
    tripStore.increaseDestinationAvailabilitySearchCounter();
    tripStore.increaseStaticDestinationAvailabilitySearchCounter();
    tripStore.updateStaysShown(0);
  });

  model.destinationAvailabilitySearchResults.subscribe((result) => {
    tripStore.clearNewDestinationAvailability();
    tripStore.clearNewDestinationStaticInfo();
    result.availability.subscribe({
      next: (availabilityResults) => {
        performanceMark('DestinationAvailabilityReceived');
        tripStore.insertNewDestinationAvailability(availabilityResults);
      },
      complete: () => {
        tripStore.updateDestinationAvailability();
        tripStore.decreaseDestinationAvailabilitySearchCounter();
      },
      error: () => {
        tripStore.decreaseDestinationAvailabilitySearchCounter();
      },
    });

    result.staticInfo.subscribe({
      next: tripStore.insertNewDestinationStaticInfo,
      complete: () => {
        tripStore.updateDestinationStaticInfo();
        tripStore.decreaseStaticDestinationAvailabilitySearchCounter();
      },
      error: () => {
        tripStore.decreaseStaticDestinationAvailabilitySearchCounter();
      },
    });
  });

  function shouldSkipNextIteration(
    newCriteria: DestinationAvailabilitySearchUserCriteria
  ): boolean {
    return (
      !newCriteria.recipe.startSearchDate &&
      !newCriteria.recipe.endSearchDate &&
      !newCriteria.day
    );
  }
}
